import React, { FC, useState, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../api';
import './top-up-pot-modal.scss';

interface RefundPotModalProps {
  isOpen: boolean;
  onClose: () => void;
  balance: number;
  currency: string;
  assistantName: string;
  onSuccess: () => void;
}

function formatCurrency(amount: number, currency: string): string {
  try {
    const isWhole = amount % 1 === 0;
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency,
      minimumFractionDigits: isWhole ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
}

function formatCurrencySymbol(currency: string): string {
  try {
    return (
      new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency,
        maximumFractionDigits: 0,
      })
        .formatToParts(0)
        .find((part) => part.type === 'currency')?.value ?? `${currency} `
    );
  } catch {
    return `${currency} `;
  }
}

const RefundPotModal: FC<RefundPotModalProps> = ({
  isOpen,
  onClose,
  balance,
  currency,
  assistantName,
  onSuccess,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const defaultAmount = String(Math.min(20, Math.round(balance * 100) / 100));
  const [amount, setAmount] = useState(defaultAmount);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = useCallback(() => {
    setAmount(defaultAmount);
    setError(null);
    onClose();
  }, [onClose, defaultAmount]);

  const parsedAmount = parseFloat(amount) || 0;
  const validAmount = parsedAmount > 0 && parsedAmount <= balance;

  const handleConfirm = useCallback(async () => {
    if (submitting || !validAmount) return;
    setSubmitting(true);
    setError(null);

    try {
      const token = await getAccessTokenSilently();
      const { data } = await api.post(
        '/credits/refund-purchase-pot',
        { amount: parsedAmount },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (data.success) {
        setAmount(defaultAmount);
        onSuccess();
      } else {
        setError(data.error || 'Refund failed. Please try again.');
      }
    } catch (err: unknown) {
      console.error('Error refunding card balance:', err);
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data
          ?.error || 'Refund failed. Please try again.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }, [
    parsedAmount,
    validAmount,
    submitting,
    defaultAmount,
    getAccessTokenSilently,
    onSuccess,
  ]);

  const handleRefundAll = useCallback(() => {
    setAmount(String(Math.round(balance * 100) / 100));
  }, [balance]);

  if (!isOpen) return null;

  return (
    <div className='topup-pot-overlay' onClick={handleClose}>
      <div className='topup-pot-modal' onClick={(e) => e.stopPropagation()}>
        <div className='topup-pot-header'>
          <p className='topup-pot-title'>
            Refund from {assistantName}&rsquo;s Card
          </p>
          <button className='topup-pot-close' onClick={handleClose}>
            <svg
              width='20'
              height='20'
              viewBox='0 0 24 24'
              fill='none'
              stroke='#374562'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <line x1='18' y1='6' x2='6' y2='18' />
              <line x1='6' y1='6' x2='18' y2='18' />
            </svg>
          </button>
        </div>

        <div className='topup-pot-body'>
          <p className='topup-pot-desc'>
            Choose how much to refund to your card. It may take a few days to
            appear on your statement.
          </p>

          <div className='topup-pot-amount-row'>
            <span className='topup-pot-amount-label'>Refund amount</span>
            <div className='topup-pot-stepper'>
              <button
                className='topup-pot-step-btn'
                onClick={() => {
                  const step = parsedAmount <= 10 ? 1 : 10;
                  const next =
                    Math.round(Math.max(0, parsedAmount - step) * 100) / 100;
                  setAmount(next === 0 ? '' : String(next));
                }}
                disabled={parsedAmount <= 0}
              >
                −
              </button>
              <div className='topup-pot-input-wrap'>
                <span className='topup-pot-currency-symbol'>
                  {formatCurrencySymbol(currency)}
                </span>
                <input
                  className='topup-pot-input'
                  type='text'
                  inputMode='decimal'
                  placeholder='0'
                  value={amount}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '' || /^\d+\.?\d{0,2}$/.test(v)) {
                      setAmount(v);
                    }
                  }}
                  autoFocus
                />
              </div>
              <button
                className='topup-pot-step-btn'
                onClick={() => {
                  const step = parsedAmount < 10 ? 1 : 10;
                  const next =
                    Math.round(Math.min(balance, parsedAmount + step) * 100) /
                    100;
                  setAmount(String(next));
                }}
                disabled={parsedAmount >= balance}
              >
                +
              </button>
            </div>
          </div>

          <div className='topup-pot-balance-hint'>
            <span>Available: {formatCurrency(balance, currency)}</span>
            <button className='topup-pot-refund-all' onClick={handleRefundAll}>
              Refund all
            </button>
          </div>

          {parsedAmount > balance && (
            <p className='topup-pot-error'>
              Amount exceeds your balance of {formatCurrency(balance, currency)}
              .
            </p>
          )}

          {error && <p className='topup-pot-error'>{error}</p>}
        </div>

        <div className='topup-pot-footer'>
          <button className='topup-pot-cancel' onClick={handleClose}>
            Cancel
          </button>
          <button
            className='topup-pot-confirm topup-pot-confirm--refund'
            onClick={handleConfirm}
            disabled={submitting || !validAmount}
          >
            {submitting
              ? 'Processing...'
              : `Refund ${validAmount ? formatCurrency(parsedAmount, currency) : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RefundPotModal;
